import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Menu, 
  Bell, 
  Search, 
  Settings, 
  User, 
  LogOut,
  ChevronDown,
  PanelLeftClose,
  PanelLeft
} from 'lucide-react';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import PlatformSelector from '../Platform/PlatformSelector';
import AIInsightDigest from './AIInsightDigest';

const Header = ({ onMenuClick, onToggleCollapse, isCollapsed }) => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, backendUrl, token } = useAuth();
  
  const [environmentInfo, setEnvironmentInfo] = useState({
    environment: 'loading',
    database_name: '',
    is_production: false,
    is_development: false
  });

  // Fetch environment from backend API
  useEffect(() => {
    const fetchEnvironment = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/health`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('🔍 Environment from backend:', data);
          
          // Priority: Use is_production flag from backend if available
          // Then check app_env, then database name
          const appEnv = data.app_env?.toLowerCase() || '';
          const dbName = data.database?.toLowerCase() || '';
          const backendSaysProduction = data.is_production === true;
          
          const isProduction = backendSaysProduction || 
                               appEnv === 'production' || 
                               appEnv === 'prod' ||
                               dbName.includes('prod') || 
                               dbName === 'payment_gateway_production';
          
          const isDevelopment = !isProduction && (
            appEnv === 'development' || 
            appEnv === 'dev' ||
            dbName.includes('dev') || 
            dbName === 'payment_gateway_dev'
          );
          
          setEnvironmentInfo({
            environment: isProduction ? 'production' : 'development',
            database_name: data.database || 'unknown',
            is_production: isProduction,
            is_development: isDevelopment
          });
          
          console.log(`${isProduction ? '🚀' : '🛠️'} Environment: ${isProduction ? 'PRODUCTION' : 'DEVELOPMENT'} (app_env: ${appEnv}, Database: ${data.database})`);
        } else {
          // Fallback to URL-based detection if API fails
          console.warn('⚠️ Failed to fetch environment from API, using URL detection');
          const hostname = window.location.hostname;
          const isProduction = hostname.includes('portal.paymentpower.net') || 
                               hostname.includes('paymentpower.net') ||
                               hostname.includes('production') ||
                               (!hostname.includes('preview') && !hostname.includes('localhost') && !hostname.includes('127.0.0.1'));
          
          setEnvironmentInfo({
            environment: isProduction ? 'production' : 'development',
            database_name: isProduction ? 'payment_gateway_production' : 'payment_gateway_dev',
            is_production: isProduction,
            is_development: !isProduction
          });
        }
      } catch (error) {
        console.error('❌ Error fetching environment:', error);
        // Fallback: check URL
        const hostname = window.location.hostname;
        const isProduction = hostname.includes('portal.paymentpower.net') || 
                             hostname.includes('paymentpower.net') ||
                             (!hostname.includes('preview') && !hostname.includes('localhost') && !hostname.includes('127.0.0.1'));
        
        setEnvironmentInfo({
          environment: isProduction ? 'production' : 'development',
          database_name: isProduction ? 'payment_gateway_production' : 'payment_gateway_dev',
          is_production: isProduction,
          is_development: !isProduction
        });
      }
    };

    fetchEnvironment();
  }, [token]);

  // Get environment display info
  const getEnvironmentDisplay = () => {
    const env = environmentInfo.environment.toLowerCase();
    
    console.log('Badge rendering with environment:', {
      environmentInfo,
      env,
      is_production: environmentInfo.is_production
    });
    
    if (env === 'production') {
      console.log('🟢 Rendering PRODUCTION badge (Prod Mode)');
      return {
        label: 'Prod',
        variant: 'default',
        className: 'bg-green-50 text-green-700 border-green-200',
        description: `Production Database`
      };
    } else if (env === 'staging') {
      return {
        label: 'Staging',
        variant: 'secondary',
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        description: `Staging Database`
      };
    } else if (env === 'development') {
      console.log('🔴 Rendering DEVELOPMENT badge (Dev Mode)');
      return {
        label: 'Dev',
        variant: 'outline',
        className: 'bg-red-50 text-red-700 border-red-200',
        description: `Development Database`
      };
    } else {
      console.log('⚪ Rendering UNKNOWN badge:', env);
      return {
        label: env === 'loading' ? '...' : env,
        variant: 'outline',
        className: 'bg-gray-50 text-gray-700 border-gray-200',
        description: `${env} Database`
      };
    }
  };

  const envDisplay = getEnvironmentDisplay();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between theme-header theme-border px-3 sm:px-6 shadow-sm">
      <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onMenuClick}
          className="lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Desktop collapse button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          className="hidden lg:flex"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <PanelLeft className="h-5 w-5" />
          ) : (
            <PanelLeftClose className="h-5 w-5" />
          )}
        </Button>
        
        <PlatformSelector />
        
        {/* Search field hidden as per user request */}
      </div>

      <div className="flex items-center space-x-1 sm:space-x-4 shrink-0">

        <div className="hidden md:flex items-center space-x-2">
          <Badge 
            key={`${environmentInfo.environment}-${environmentInfo.is_production}`}
            variant={envDisplay.variant} 
            className={envDisplay.className}
            title={envDisplay.description}
          >
            {envDisplay.label}
          </Badge>
          <span className="text-sm text-muted-foreground">Mode</span>
        </div>

        {/* AI Insight Digest popover */}
        <AIInsightDigest />

        {/* Notification bell - no counter */}
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className={`flex items-center space-x-2 ${
                isAdmin ? 'bg-red-50 border border-red-200 hover:bg-red-100' : ''
              }`}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className={isAdmin ? 'bg-red-100 text-red-800' : ''}>
                  {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium">{user?.name || 'User'}</div>
                <div className={`text-xs ${isAdmin ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                  {isAdmin ? 'Admin User' : 'User'}
                </div>
              </div>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/dashboard/settings?tab=profile')} className="cursor-pointer">
              <User className="h-4 w-4 mr-2" />
              Profile
            </DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} className="cursor-pointer">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-red-600 cursor-pointer"
              onClick={logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;